import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Button,
    Chip,
    Grid,
    IconButton,
    Typography
} from "@mui/material/";
import useColumnsRows from "./useColumnsRows";
import actions from "../../../Store/actions";
import { strings } from "../../../common/i18n";
import { helpers } from "../../../common/lib";
import MatReactTable from "../../../common/components/MaterialReactTable";
import AccountForm from "./AccountForm";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import styles from "./styles";
import Loading from "../../../common/components/Loading";
import { logToSentry } from "../../../config/Sentry";

/**
 * User Component
 */
export default () => {
    const { accounts } = useSelector((state) => state);
    const { item } = accounts;
    const { columns, data } = useColumnsRows();

    const dispatch = useDispatch();
    let navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const params = useParams();

    const [expanded, setExpanded] = useState("p1");
    const [disabled, setDisabled] = useState(true);
    const [submitted, setSubmitted] = useState(false);

    const groups = accounts.item ? accounts.item.groups : [];

    useEffect(() => {
        if (params.userId && accounts.data.length) {
            const account = helpers.getListItemForId(accounts, params.userId);
            dispatch(actions.accounts.create.itemSet(account));
        } else {
            dispatch(actions.accounts.create.itemAdd());
        }
        return () => {
            dispatch(actions.accounts.create.itemReset());
        };
    }, []);

    /** Update when the disable button change is triggered  */
    useEffect(() => {}, [disabled]);

    const onAccountFormSubmit = async (account: any, created: boolean) => {
        try {
            setLoading(true);
            const locationsByGroup = groups.reduce(
                (aggregate, { id, name }) => {
                    const locationIds = [];
                    data.forEach((row) => {
                        row[name] &&
                            row[name].selected &&
                            locationIds.push(row.locationId);
                    });
                    if (locationIds.length > 0) {
                        aggregate[id] = locationIds;
                    }
                    return aggregate;
                },
                {}
            );
            const next = Object.entries(locationsByGroup).map(
                ([key, value]) => ({
                    group: Number(key),
                    location: value,
                    user: account.id
                })
            );
            if (next.length) {
                await dispatch(actions.userLocations.create.postRequest(next));
            } else {
                await dispatch(
                    actions.userLocations.create.deletePostRequest([account.id])
                );
            }
            await dispatch(actions.accounts.create.getRequest());
            await dispatch(actions.userLocations.create.getRequest());
            dispatch(actions.accounts.create.itemReset());
            setLoading(false);
        } catch (error) {
            console.error(error);
            logToSentry("On account form submit:", { error });
        }
        setLoading(false);
        const message = strings(
            created ? "createdSuccessfully" : "updatedSuccessfully"
        );
        dispatch(actions.toast.create.success(message));
    };

    const handleChange = (panel) => (event, newExpanded) => {
        setExpanded(newExpanded ? panel : false);
    };
    /** On save of the form  */
    const onSave = async () => {
        setSubmitted(true);
        if (!disabled) {  // disabled==true means formValid
            return;
        }

        try {
            setLoading(true);
            const isUpdate = Boolean(item.id);
            const next = {
                ...item,
                groups: item.groups.map((group) => group.id)
            };
            const res = await dispatch(
                actions.accounts.create.postRequest([next])
            );
            onAccountFormSubmit &&
                onAccountFormSubmit(res.accounts[0], !isUpdate);

            navigate(`..`);
            // await dispatch(actions.accounts.create.getRequest());
            setLoading(false);
        } catch (error) {
            const errStr = error.toString();
            logToSentry("On save accounts:", { error });
            dispatch(actions.toast.create.error(strings("errors.updateUser")));
            console.error(error);
        }
    };
    /** Email request   */
    const emailRequest = async () => {
        try {
            await dispatch(
                actions.userWelcomeRequest.create.postRequest({
                    email: accounts.item.email
                })
            );
            dispatch(
                actions.toast.create.success(strings("messages.emailSent"))
            );
        } catch (error) {
            const errStr = error.toString();
            logToSentry("On email request:", { error });
            dispatch(actions.toast.create.error(strings("errors.emailSent")));
            console.log(error);
        }
    };

    const onBack = () => {
        dispatch(actions.accounts.create.itemReset());
        navigate("..");
    };

    return (
        <>
            {loading && <Loading loadingText="loadingUsers" />}
            {accounts.item && (
                <>
                <Box sx={{ display: "flex", alignItems: "center", px: 1, py: 0.5 }}>
                    <IconButton size="small" onClick={onBack}>
                        <ArrowBackIosNewIcon fontSize="small" color="primary" />
                    </IconButton>
                    <Typography variant="body2" color="primary" sx={{ ml: 0.5 }}>
                        {strings("users")}
                    </Typography>
                </Box>
                <Grid sx={styles.fullHeight} container>
                    <Grid sx={styles.fullHeight} item xs={8}>
                        <MatReactTable
                            persistKey="userAccount"
                            columns={columns}
                            data={data}
                            layoutMode="grid-no-grow"
                            enableColumnResizing
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <Box
                            sx={{
                                width: "100%",
                                height: "100%",
                                position: "relative"
                            }}
                        >
                            <Accordion
                                expanded={expanded === "p1"}
                                onChange={handleChange("p1")}
                                sx={{
                                    zIndex: 2
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls="p1-content"
                                    id="p1-header"
                                >
                                    <Chip
                                        sx={styles.chipStyles}
                                        icon={
                                            <AccountCircleOutlinedIcon color="primary" />
                                        }
                                        label={strings("accountDetails")}
                                    />
                                </AccordionSummary>
                                <AccordionDetails>
                                    <AccountForm
                                        onSubmit={onAccountFormSubmit}
                                        setDisabled={setDisabled}
                                        submitted={submitted}
                                    />
                                </AccordionDetails>
                            </Accordion>
                            <Accordion
                                sx={{
                                    zIndex: 2
                                }}
                                expanded={expanded === "p2"}
                                onChange={handleChange("p2")}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls="p2-content"
                                    id="p2-header"
                                >
                                    <Chip
                                        sx={styles.chipStyles}
                                        icon={
                                            <MiscellaneousServicesIcon color="primary" />
                                        }
                                        label={strings("accountServices")}
                                    />
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Button
                                        variant="outlined"
                                        startIcon={<EmailOutlinedIcon />}
                                        onClick={emailRequest}
                                        fullWidth
                                        disableElevation={false}
                                        sx={styles.buttonStyles}
                                    >
                                        {strings("sendWelcomeEmail")}
                                    </Button>
                                </AccordionDetails>
                            </Accordion>
                            <Button
                                variant="outlined"
                                size="medium"
                                fullWidth
                                sx={{
                                    my: 4,
                                }}
                                disabled={loading}
                                onClick={onSave}
                            >
                                {strings("save")}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
                </>
            )}
        </>
    );
};
//TODO;
//1.Convert Datatable to Datagridpro
//2. Layout issue still exist this is temporary fix for save button



